import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const TaskDistributionCharts = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [data, setData] = useState(null);       // レスポンスデータ
  const [loading, setLoading] = useState(true); // ローディング状態
  const [error, setError] = useState(null);     // エラー状態

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    setLoading(true);
    setError(null);

    axios.get(`${API_BASE}/api/report/tag-usage`, {
      withCredentials: true,
      params: {
        days: timeRange === '7days' ? 7 : 28,
      }
    }).then((res) => {
      setData(res.data);
      setLoading(false);
    }).catch((err) => {
      console.error('Fetch error:', err);
      setError(err);
      setLoading(false);
    });
  }, [timeRange]);

  const chartSize = isMobile ? 200 : isTablet ? 250 : 300;

  if (loading) {
    return (
      <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
        <CardContent>
          <Typography variant="h6">Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
        <CardContent>
          <Typography color="error" variant="h6">
            エラーが発生しました:
          </Typography>
          <Typography variant="body1">{error.message}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data[timeRange]) {
    return (
      <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
        <CardContent>
          <Typography variant="h6">
            データがありません。
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const { incomplete, completed } = data[timeRange];

  // PieChart用の描画コンポーネント
  const RenderPieChart = ({ pieData, title }) => (
    <Box sx={{ width: isMobile ? '100%' : '48%', mb: isMobile ? 4 : 0 }}>
      <Typography variant={isMobile ? 'subtitle1' : 'h6'} align="center">
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={chartSize}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={chartSize / 3}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Card sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
      <CardContent>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="div"
          gutterBottom
        >
          タグ使用割合
        </Typography>

        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(event, newTimeRange) => {
            if (newTimeRange !== null) {
              setTimeRange(newTimeRange);
            }
          }}
          aria-label="time range"
          size={isMobile ? 'small' : 'medium'}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="7days" aria-label="過去7日">
            過去7日間
          </ToggleButton>
          <ToggleButton value="28days" aria-label="過去28日">
            過去28日間
          </ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* 未完了タスク */}
          <RenderPieChart pieData={incomplete} title="未完了タスク" />

          {/* 完了タスク */}
          <RenderPieChart pieData={completed} title="完了タスク" />
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskDistributionCharts;
